// =============================================================================
// screens/VoiceRoomsScreen.tsx — Voice Rooms list + "Start a Room" creation flow
//
// Bare-RN port of apps/cryb-native/app/voice/index.tsx (P3-5 CALLING group).
//
// MIGRATION (blueprint §1):
//   - expo-router (useRouter/Stack.Screen)
//       -> react-navigation: useNavigation(). The card tap + post-create jump
//          router.push('/voice/:id') -> navigation.navigate('VoiceRoom',{id}).
//          The inline <Stack.Screen> header (title 'Voice Rooms' + the help/+
//          headerRight buttons) is re-applied via navigation.setOptions in a
//          layout effect, since this screen is registered on the RootStack.
//   - @expo/vector-icons (Ionicons) -> components/ui/Icon.
//
// PRESERVED VERBATIM:
//   - GET /voice/rooms RAW-LiveKit-room normalization (metadata JSON parse,
//     custom_room_ prefix strip), POST /voice/rooms create ({ name, topic? }) ->
//     { room:{id} } then jump into the room, defensive list guards.
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  Alert,
  TextInput,
  Modal,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale, shadowTokens,
} from '../constants/theme';
import { GlassCard, GlassButton, GlassSurface } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import ScreenHeading from '../components/ui/ScreenHeading';
import { tabularNums } from '../lib/format';
import { api } from '../services/api';

type Nav = NativeStackNavigationProp<RootStackParamList, 'VoiceRooms'>;

// Shape of a room AFTER client-side normalisation (see normalizeRoom).
// GET /voice/rooms actually returns RAW LiveKit room objects — see RawLiveKitRoom.
interface VoiceRoom {
  id: string;
  title: string;
  host?: {
    username: string;
    display_name: string;
    avatar?: string;
  };
  participants_count: number;
  max_participants?: number;
  is_live: boolean;
  topic?: string;
  speakers?: { username: string; display_name: string; avatar?: string }[];
}

// Raw LiveKit room object as actually returned by GET /voice/rooms. The useful
// values (roomId/name/topic) live in the JSON-STRING `metadata`, NOT at top level.
interface RawLiveKitRoom {
  sid?: string;
  name?: string; // e.g. "custom_room_room_<ts>_<owner>"
  numParticipants?: number;
  maxParticipants?: number;
  metadata?: string; // JSON string: { roomId, name, topic?, ownerId, ... }
}

// Normalise one raw LiveKit room into the VoiceRoom shape the UI expects.
// metadata is a JSON string and may be missing/malformed, so parse defensively.
function normalizeRoom(raw: RawLiveKitRoom): VoiceRoom {
  let meta: any = {};
  if (raw?.metadata && typeof raw.metadata === 'string') {
    try {
      meta = JSON.parse(raw.metadata) ?? {};
    } catch {
      meta = {};
    }
  }
  // Prefer the roomId from metadata; otherwise derive it from the LiveKit room
  // name by stripping the "custom_room_" prefix the backend prepends.
  const derivedId =
    typeof raw?.name === 'string' ? raw.name.replace(/^custom_room_/, '') : '';
  const id = meta?.roomId || derivedId || raw?.sid || '';
  const count = typeof raw?.numParticipants === 'number' ? raw.numParticipants : 0;
  return {
    id,
    title: meta?.name || 'Voice Room',
    topic: typeof meta?.topic === 'string' ? meta.topic : undefined,
    participants_count: count,
    max_participants: raw?.maxParticipants,
    is_live: true,
  };
}

export default function VoiceRoomsScreen() {
  const navigation = useNavigation<Nav>();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const contentMaxWidth = layout === 'desktop' ? 900 : layout === 'tablet' ? 760 : undefined;
  const containerStyle = layout !== 'phone'
    ? { maxWidth: contentMaxWidth, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  // Responsive room grid: 1 col phone, 2 tablet, 3 desktop.
  const gridColumns = layout === 'desktop' ? 3 : layout === 'tablet' ? 2 : 1;
  const [rooms, setRooms] = useState<VoiceRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTopic, setNewTopic] = useState('');
  // Stage room: only the host + promoted speakers can talk (others listen + raise
  // a hand). Off = classic room where everyone can talk.
  const [newStage, setNewStage] = useState(false);
  const [creating, setCreating] = useState(false);

  // Header (was inline <Stack.Screen options>): title + help/about + "start a
  // room" headerRight actions. setOptions in a layout effect so the headerRight
  // closures see the live setShowCreate.
  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      title: 'Voice Rooms',
      headerStyle: { backgroundColor: colors.bg },
      headerTintColor: colors.ink,
      headerShadowVisible: false,
      headerTitleStyle: { ...fonts.semibold, fontSize: 17 },
      headerRight: () => (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <TouchableOpacity
            onPress={() => Alert.alert('Voice Rooms', 'Join or create live audio rooms. Talk with the community about crypto, trading, and anything Web3.')}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="About Voice Rooms"
          >
            <Ionicons name="help-circle-outline" size={18} color={colors.muted} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setShowCreate(true)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel="Start a new voice room"
          >
            <Ionicons name="add-circle-outline" size={24} color={colors.primary} />
          </TouchableOpacity>
        </View>
      ),
    });
  }, [navigation]);

  // --- Data loading ---------------------------------------------------------

  const fetchRooms = useCallback(async () => {
    setError(null);
    // GET /voice/rooms returns { rooms: RawLiveKitRoom[] } (api() unwraps to res.data).
    // Each entry is a RAW LiveKit room — normalize so the UI gets id/title/topic etc.
    const res = await api<{ rooms?: RawLiveKitRoom[] }>('/voice/rooms');
    if (res.success && res.data) {
      const raw = Array.isArray(res.data.rooms) ? res.data.rooms : [];
      setRooms(raw.map(normalizeRoom).filter((r) => !!r.id));
    } else if (!res.success) {
      setError(res.error || 'Failed to load voice rooms.');
    }
    setLoading(false);
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchRooms();
    setRefreshing(false);
  }, [fetchRooms]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Create a room, then jump straight into it. Title is required; topic optional
  // (sent as undefined when blank so the backend stores no empty string).
  async function handleCreateRoom() {
    if (!newTitle.trim()) {
      Alert.alert('Error', 'Please enter a room title.');
      return;
    }
    setCreating(true);
    // POST /voice/rooms requires `name` (not `title`) and returns
    // { room: { id, ... }, token } (api() unwraps one level to res.data).
    const res = await api<{ room?: { id?: string } }>('/voice/rooms', {
      method: 'POST',
      body: JSON.stringify({
        name: newTitle.trim(),
        topic: newTopic.trim() || undefined,
        stage: newStage,
      }),
    });
    setCreating(false);

    const roomId = res.data?.room?.id;
    if (res.success && roomId) {
      setShowCreate(false);
      setNewTitle('');
      setNewTopic('');
      setNewStage(false);
      navigation.navigate('VoiceRoom', { id: roomId });
    } else {
      Alert.alert('Error', res.error || 'Failed to create room.');
    }
  }

  // One room card: live badge, title/topic, host, a clipped speakers stack
  // (first 4 + "+N"), and a footer with the listener count and Join affordance.
  function renderRoomCard({ item }: { item: VoiceRoom }) {
    return (
      <View style={gridColumns > 1 ? styles.gridCell : styles.cardWrap}>
        <GlassCard
          padding={spacing.lg}
          radius={radius.xl}
          style={styles.card}
          onPress={() => navigation.navigate('VoiceRoom', { id: item.id })}
          accessibilityLabel={`Voice room ${item.title || 'Untitled'}, ${item.participants_count ?? 0} listening`}
        >
          {/* Live indicator */}
          {item.is_live && (
            <View style={styles.liveBadge}>
              <View style={styles.liveDot} />
              <Text style={styles.liveText}>LIVE</Text>
            </View>
          )}

          {/* Title */}
          <Text style={styles.roomTitle} numberOfLines={2}>
            {item.title || 'Untitled Room'}
          </Text>

          {/* Topic */}
          {item.topic ? (
            <Text style={styles.roomTopic} numberOfLines={1}>
              {item.topic}
            </Text>
          ) : null}

          {/* Host */}
          <View style={styles.hostRow}>
            <Ionicons name="mic" size={14} color={colors.primary} />
            <Text style={styles.hostName} numberOfLines={1}>
              {item.host?.display_name || item.host?.username || 'Unknown host'}
            </Text>
          </View>

          {/* Speakers preview */}
          {Array.isArray(item.speakers) && item.speakers.length > 0 && (
            <View style={styles.speakersRow}>
              {item.speakers.slice(0, 4).map((s, i) => (
                <View
                  key={s?.username ?? `speaker-${i}`}
                  style={[
                    styles.speakerAvatar,
                    { marginLeft: i > 0 ? -spacing.sm : 0, zIndex: 10 - i },
                  ]}
                >
                  <Text style={styles.speakerLetter}>
                    {(s?.display_name || s?.username || 'U').charAt(0).toUpperCase()}
                  </Text>
                </View>
              ))}
              {item.speakers.length > 4 && (
                <Text style={styles.moreSpeakers}>
                  +{item.speakers.length - 4}
                </Text>
              )}
            </View>
          )}

          {/* Footer */}
          <View style={styles.cardFooter}>
            <View style={styles.participantsRow}>
              <Ionicons name="people" size={14} color={colors.muted} />
              <Text style={styles.participantsText}>
                {item.participants_count ?? 0}
                {item.max_participants ? `/${item.max_participants}` : ''} listening
              </Text>
            </View>
            <View style={styles.joinBtn}>
              <Ionicons name="enter-outline" size={16} color={colors.white} />
              <Text style={styles.joinBtnText}>Join</Text>
            </View>
          </View>
        </GlassCard>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Living animated background (design-system): deep premium glass surface. */}
      <ParticleBackground />

      {loading ? (
        <View style={[styles.listContent, containerStyle]}>
          {[0, 1, 2].map((i) => (
            <GlassCard key={i} style={styles.skeletonCard} padding={spacing.lg} radius={radius.xl}>
              <View style={[styles.skeletonLine, { width: '30%', height: 16, marginBottom: spacing.md }]} />
              <View style={[styles.skeletonLine, { width: '70%' }]} />
              <View style={[styles.skeletonLine, styles.skeletonLineThin, { width: '45%', marginTop: spacing.sm }]} />
            </GlassCard>
          ))}
        </View>
      ) : error ? (
        <View style={styles.emptyWrap}>
          <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
            <Asset3D name="error" size={110} style={{ marginBottom: spacing.md }} />
            <Text style={styles.emptyText}>Couldn't load rooms</Text>
            <Text style={styles.emptySubtext}>{error}</Text>
            <GlassButton
              title="Retry"
              icon="refresh-outline"
              onPress={fetchRooms}
              fullWidth
              style={styles.emptyCta}
            />
          </GlassCard>
        </View>
      ) : (
        <FlatList
          data={Array.isArray(rooms) ? rooms : []}
          keyExtractor={(item, index) => item?.id ?? `room-${index}`}
          renderItem={renderRoomCard}
          numColumns={gridColumns}
          key={`cols-${gridColumns}`}
          columnWrapperStyle={gridColumns > 1 ? styles.gridRow : undefined}
          ListHeaderComponent={
            rooms.length > 0 ? (
              <Text style={styles.resultsLabel}>Live now  ·  {rooms.length}</Text>
            ) : null
          }
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <GlassCard style={styles.emptyCard} padding={spacing.xl} radius={radius.xl}>
                <Asset3D name="orb" size={110} style={{ marginBottom: spacing.md }} />
                <Text style={styles.emptyText}>No active rooms</Text>
                <Text style={styles.emptySubtext}>
                  Start a voice room to discuss crypto, markets, and more.
                </Text>
                <GlassButton
                  title="Start a Room"
                  icon="mic"
                  onPress={() => setShowCreate(true)}
                  fullWidth
                  style={styles.emptyCta}
                />
              </GlassCard>
            </View>
          }
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, containerStyle]}
        />
      )}

      {/* Create Room Modal — title-required sheet; Start is disabled until valid */}
      <Modal
        visible={showCreate}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCreate(false)}
      >
        <KeyboardAvoidingView
          style={styles.modalContainer}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <ParticleBackground />
          <View style={styles.modalHeader}>
            <ScreenHeading
              kicker="VOICE ROOMS"
              title="Start a Room"
              subtitle="Go live and talk crypto with the community."
              style={styles.modalHeading}
            />
            <TouchableOpacity onPress={() => setShowCreate(false)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityRole="button" accessibilityLabel="Close" style={styles.modalCloseBtn}>
              <Ionicons name="close" size={24} color={colors.ink} />
            </TouchableOpacity>
          </View>

          <View style={[styles.modalBody, containerStyle]}>
            <Text style={styles.modalLabel}>Room Title <Text style={{ color: colors.error }}>*</Text></Text>
            <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.modalInput}>
              <TextInput
                style={styles.modalInputText}
                placeholder="e.g. BTC to 100k? Let's discuss"
                placeholderTextColor={colors.muted}
                value={newTitle}
                onChangeText={setNewTitle}
                maxLength={100}
                autoFocus
              />
            </GlassSurface>

            <Text style={styles.modalLabel}>Topic (optional)</Text>
            <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.modalInput}>
              <TextInput
                style={styles.modalInputText}
                placeholder="e.g. Crypto, DeFi, NFTs"
                placeholderTextColor={colors.muted}
                value={newTopic}
                onChangeText={setNewTopic}
                maxLength={60}
              />
            </GlassSurface>

            <Text style={styles.modalLabel}>Room type</Text>
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={() => setNewStage((s) => !s)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 6, marginBottom: 8 }}
              accessibilityRole="switch"
              accessibilityState={{ checked: newStage }}
            >
              <Ionicons name={newStage ? 'checkbox' : 'square-outline'} size={24} color={newStage ? colors.primary : colors.muted} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.modalInputText, { fontWeight: '700' }]}>Stage room</Text>
                <Text style={{ color: colors.muted, fontSize: 12, marginTop: 2 }}>
                  Only you + approved speakers can talk; others listen and can raise a hand.
                </Text>
              </View>
            </TouchableOpacity>

            <GlassButton
              title="Start Room"
              icon="mic"
              onPress={handleCreateRoom}
              loading={creating}
              disabled={!newTitle.trim() || creating}
              fullWidth
              size="lg"
              style={styles.createBtn}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // Card = shared GlassCard; this only stretches it inside the cell.
  card: {
    flex: 1,
  },
  cardWrap: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.error + '15',
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radius.full,
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: radius.full,
    backgroundColor: colors.error,
  },
  liveText: {
    ...typographyScale.caption,
    ...fonts.bold,
    fontSize: 10,
    letterSpacing: 0.6,
    color: colors.error,
  },
  roomTitle: {
    ...typographyScale.h3,
    ...fonts.bold,
    color: colors.ink,
  },
  roomTopic: {
    ...typographyScale.body,
    fontSize: 13,
    color: colors.muted,
    marginTop: spacing.xs,
  },
  hostRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  hostName: {
    ...typographyScale.caption,
    ...fonts.semibold,
    fontSize: 13,
    color: colors.primary,
  },
  speakersRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing.md,
  },
  speakerAvatar: {
    width: 28,
    height: 28,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.white,
  },
  speakerLetter: {
    ...typographyScale.caption,
    ...fonts.bold,
    fontSize: 11,
    color: colors.primary,
  },
  moreSpeakers: {
    ...typographyScale.caption,
    color: colors.muted,
    marginLeft: spacing.sm,
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.cardBorder,
  },
  participantsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  participantsText: {
    ...typographyScale.caption,
    ...tabularNums,
    color: colors.muted,
  },
  joinBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    gap: spacing.xs,
    ...shadowTokens.bar,
    shadowColor: colors.primary,
  },
  joinBtnText: {
    ...typographyScale.caption,
    ...fonts.semibold,
    fontSize: 13,
    color: colors.white,
  },
  // Multi-column grid (tablet/desktop).
  gridRow: {
    paddingHorizontal: layoutTokens.gutter,
    gap: spacing.md,
    marginBottom: spacing.md,
    alignItems: 'stretch',
  },
  gridCell: {
    flex: 1,
  },
  resultsLabel: {
    ...typographyScale.caption,
    ...fonts.bold,
    ...tabularNums,
    color: colors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  listContent: {
    paddingTop: spacing.md,
    // Clear the floating glass tab bar so the last room card is visible.
    paddingBottom: layoutTokens.bottomNavInset,
    flexGrow: 1,
  },
  // --- Loading skeleton ---
  skeletonCard: {
    marginHorizontal: layoutTokens.gutter,
    marginBottom: spacing.md,
  },
  skeletonLine: {
    height: 12,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '14',
  },
  skeletonLineThin: {
    height: 10,
    backgroundColor: colors.primary + '0D',
  },
  // --- Empty / error states ---
  emptyWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: spacing.xxxl + spacing.xxl,
    paddingHorizontal: spacing.xl,
  },
  emptyCard: {
    width: '100%',
    maxWidth: 340,
    alignItems: 'center',
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyCta: {
    marginTop: spacing.lg,
  },
  // Modal
  modalContainer: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  modalHeader: {
    flexDirection: 'row',
    // ScreenHeading is a block (kicker + gradient title + subtitle); anchor the
    // row to the top so the close button doesn't vertically mis-center against it.
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  modalHeading: {
    flexShrink: 1,
    paddingRight: spacing.md,
  },
  modalCloseBtn: {
    marginTop: spacing.xs,
  },
  modalBody: {
    padding: spacing.lg,
    width: '100%',
  },
  modalLabel: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  modalInput: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    minHeight: 48,
  },
  modalInputText: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink,
    padding: 0,
  },
  createBtn: {
    marginTop: spacing.xl,
  },
});
